import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  FaSync,
  FaChartLine,
  FaExclamationTriangle,
  FaClock,
} from 'react-icons/fa';
import api from '../../libs/axios';
import { useAuth } from '../../provider/auth.provider';
import {
  DashboardResponse,
  AdminDashboard,
  TeamCoordinatorDashboard,
  TechnicalAssociateDashboard,
  MISDashboard,
  SiteEngineerDashboard,
  ReportMakerDashboard,
  ReportValidatorDashboard,
  HRDashboard,
  BillingManagerDashboard,
  AccountsDashboard,
  ProjectManagerDashboard,
} from '../../types/dashboard';
import { SkeletonCard, SkeletonChart, SkeletonTable } from './components/ui';
import { PersonalDashboard } from './components/personal-sections';
import {
  AdminDashboardView,
  TeamCoordinatorView,
  SiteEngineerView,
  ReportMakerView,
  ReportValidatorView,
  HRView,
  BillingManagerView,
  AccountsView,
  ProjectManagerView,
  GenericDashboardView,
} from './components/role-dashboards';
import MISView from './components/MISview';
import { UpcomingTeamLeavesCard } from './components/upcoming-team-leaves';
import TechnicalAssociateView from './components/technical-associate-view';
import { TeamCoordinatorSpecSections } from './components/team-coordinator-spec-sections';
import ProjectManagerSpecSections from './components/project-manager-spec-sections';

// Role ID constants
const ROLE_IDS = {
  SUPER_ADMIN: 1,
  ADMIN: 2,
  PROJECT_MANAGER: 3,
  TEAM_COORDINATOR: 4,
  TECHNICAL_ASSOCIATE: 5,
  MIS: 6,
  SITE_ENGINEER: 7,
  SITE_DESIGNER: 8,
  REPORT_MAKER: 9,
  REPORT_VALIDATOR: 10,
  TEAM_LEAD: 11,
  PROJECT_LEAD: 12,
  QUALITY_STAFF: 13,
  SITE_SUPERVISOR: 14,
  SUPPORT_STAFF: 15,
  BILLING_MANAGER: 16,
  HR: 17,
  ACCOUNTS: 18,
  VENDOR_MANAGER: 19,
  IT_TOOLS: 20,
  SAFETY_CONTROL: 21,
};

// Auto-refresh interval (5 minutes)
const REFRESH_INTERVAL = 5 * 60 * 1000;

export default function Dashboard() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dashboardData, setDashboardData] = useState<DashboardResponse['data'] | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const fetchDashboard = useCallback(async (showRefreshing = false) => {
    if (!user) return;

    try {
      if (showRefreshing) {
        setIsRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      // Site Engineers use a different endpoint
      const endpoint =
        user.role?.id === ROLE_IDS.SITE_ENGINEER
          ? '/analytics/engineer'
          : '/analytics/dashboard';

      const response = await api.get<DashboardResponse>(endpoint);

      if (response.data.success) {
        setDashboardData(response.data.data);
        setLastUpdated(new Date());
      } else {
        setError(response.data.message || 'Failed to load dashboard data');
      }
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 400) {
        // Site Engineer role - wrong endpoint, redirect to engineer endpoint
        setError('Please use the Site Engineer dashboard.');
      } else if (status === 403) {
        setError('Dashboard not available for your role.');
      } else if (status === 404) {
        setError('Role configuration not found.');
      } else {
        setError(err?.response?.data?.message || 'Failed to load dashboard data');
      }
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, [user]);

  // Initial fetch
  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  // Auto-refresh
  useEffect(() => {
    const interval = setInterval(() => {
      fetchDashboard(true);
    }, REFRESH_INTERVAL);

    return () => clearInterval(interval);
  }, [fetchDashboard]);

  const handleManualRefresh = () => {
    fetchDashboard(true);
  };

  // Render role-specific dashboard
  const renderRoleDashboard = () => {
    if (!dashboardData || !user) return null;

    // Site Engineer dashboard (/analytics/engineer) doesn't include role_id in response
    const roleId = dashboardData.role_id ?? user?.role?.id;
    const roleName = dashboardData.role_name ?? user?.role?.name ?? '';

    switch (roleId) {
      case ROLE_IDS.SUPER_ADMIN:
        return <AdminDashboardView data={dashboardData as unknown as AdminDashboard} />;

      case ROLE_IDS.PROJECT_MANAGER:
        return (
          <>
            {dashboardData.my_attendance && (
              <PersonalDashboard
                attendance={dashboardData.my_attendance}
                leaves={dashboardData.my_leaves}
                claims={dashboardData.my_claims}
              />
            )}
            <ProjectManagerSpecSections data={dashboardData} />
            <ProjectManagerView data={dashboardData as unknown as ProjectManagerDashboard} />
          </>
        );

      case ROLE_IDS.TEAM_COORDINATOR:
        return (
          <>
            {dashboardData.my_attendance && (
              <PersonalDashboard
                attendance={dashboardData.my_attendance}
                leaves={dashboardData.my_leaves}
                claims={dashboardData.my_claims}
              />
            )}

            <TeamCoordinatorSpecSections data={dashboardData} />
            <TeamCoordinatorView data={dashboardData as unknown as TeamCoordinatorDashboard} />
          </>
        );

      case ROLE_IDS.TECHNICAL_ASSOCIATE:
        return (
          <>
            {dashboardData.my_attendance && (
              <PersonalDashboard
                attendance={dashboardData.my_attendance}
                leaves={dashboardData.my_leaves}
                claims={dashboardData.my_claims}
              />
            )}
            <TechnicalAssociateView data={dashboardData as unknown as TechnicalAssociateDashboard} />
          </>
        );

      case ROLE_IDS.MIS:
        return (
          <>
            {dashboardData.my_attendance && (
              <PersonalDashboard
                attendance={dashboardData.my_attendance}
                leaves={dashboardData.my_leaves}
                claims={dashboardData.my_claims}
              />
            )}
            <MISView data={dashboardData as unknown as MISDashboard} />
          </>
        );

      case ROLE_IDS.SITE_ENGINEER:
        return <SiteEngineerView data={dashboardData as unknown as SiteEngineerDashboard} />;

      case ROLE_IDS.SITE_DESIGNER:
      case ROLE_IDS.REPORT_MAKER:
        return (
          <>
            {dashboardData.my_attendance && (
              <PersonalDashboard
                attendance={dashboardData.my_attendance}
                leaves={dashboardData.my_leaves}
                claims={dashboardData.my_claims}
              />
            )}
            <ReportMakerView data={dashboardData as unknown as ReportMakerDashboard} />
          </>
        );

      case ROLE_IDS.REPORT_VALIDATOR:
        return (
          <>
            {dashboardData.my_attendance && (
              <PersonalDashboard
                attendance={dashboardData.my_attendance}
                leaves={dashboardData.my_leaves}
                claims={dashboardData.my_claims}
              />
            )}
            <ReportValidatorView data={dashboardData as unknown as ReportValidatorDashboard} />
          </>
        );

      case ROLE_IDS.HR:
        return (
          <>
            {dashboardData.my_attendance && (
              <PersonalDashboard
                attendance={dashboardData.my_attendance}
                leaves={dashboardData.my_leaves}
                claims={dashboardData.my_claims}
              />
            )}
            <HRView data={dashboardData as unknown as HRDashboard} />
          </>
        );

      case ROLE_IDS.BILLING_MANAGER:
        return (
          <>
            {dashboardData.my_attendance && (
              <PersonalDashboard
                attendance={dashboardData.my_attendance}
                leaves={dashboardData.my_leaves}
                claims={dashboardData.my_claims}
              />
            )}
            <BillingManagerView data={dashboardData as unknown as BillingManagerDashboard} />
          </>
        );

      case ROLE_IDS.ACCOUNTS:
        return (
          <>
            {dashboardData.my_attendance && (
              <PersonalDashboard
                attendance={dashboardData.my_attendance}
                leaves={dashboardData.my_leaves}
                claims={dashboardData.my_claims}
              />
            )}
            <AccountsView data={dashboardData as unknown as AccountsDashboard} />
          </>
        );

      // Default for other roles with personal sections
      default:
        return (
          <>
            {dashboardData.my_attendance && (
              <PersonalDashboard
                attendance={dashboardData.my_attendance}
                leaves={dashboardData.my_leaves}
                claims={dashboardData.my_claims}
              />
            )}
            <GenericDashboardView roleName={roleName} data={dashboardData} />
          </>
        );
    }
  };

  // Loading skeleton
  if (loading) {
    return (
      <div className="space-y-6">
        <Helmet>
          <title>Dashboard | Eminence</title>
        </Helmet>

        {/* Header Skeleton */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <div className="h-7 bg-gray-200 rounded w-48 animate-pulse" />
            <div className="h-4 bg-gray-200 rounded w-32 mt-2 animate-pulse" />
          </div>
          <div className="h-10 bg-gray-200 rounded w-24 animate-pulse" />
        </div>

        {/* Stats Skeleton */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <SkeletonCard key={i} />
          ))}
        </div>

        {/* Charts Skeleton */}
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          <SkeletonChart />
          <SkeletonChart />
          <SkeletonChart />
        </div>

        {/* Tables Skeleton */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <SkeletonTable />
          <SkeletonTable />
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <Helmet>
          <title>Dashboard | Eminence</title>
        </Helmet>

        <div className="flex flex-col items-center justify-center min-h-[400px]">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mb-4">
            <FaExclamationTriangle className="w-10 h-10 text-red-500" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Unable to Load Dashboard</h2>
          <p className="text-gray-600 text-center max-w-md mb-6">{error}</p>
          <button
            onClick={() => fetchDashboard()}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
          >
            <FaSync className="w-4 h-4" />
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Helmet>
        <title>Dashboard | Eminence</title>
      </Helmet>

      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          {/* <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FaChartLine className="w-6 h-6 text-blue-600" />
            Dashboard
          </h1> */}
          <p className="text-sm text-gray-500 mt-1">
            Welcome back, <span className="font-medium">{user?.first_name}</span>
            {dashboardData?.role_name && (
              <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                {dashboardData.role_name}
              </span>
            )}
          </p>
        </div>

        <div className="flex items-center gap-4">
          {lastUpdated && (
            <span className="text-xs text-gray-500 flex items-center gap-1">
              <FaClock className="w-3 h-3" />
              Updated {lastUpdated.toLocaleTimeString()}
            </span>
          )}
          <button
            onClick={handleManualRefresh}
            disabled={isRefreshing}
            className={`px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2 text-sm font-medium ${
              isRefreshing ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            <FaSync className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            {isRefreshing ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Refreshing indicator */}
      {isRefreshing && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2 flex items-center gap-2 text-sm text-blue-700">
          <FaSync className="w-4 h-4 animate-spin" />
          Refreshing dashboard data...
        </div>
      )}

      {/* Dashboard Content */}
      <div className="space-y-8">
        <UpcomingTeamLeavesCard data={(dashboardData as any)?.upcoming_team_leaves} />
        {renderRoleDashboard()}
      </div>

      {/* Footer with generation time */}
      {dashboardData?.generated_at && (
        <div className="text-center text-xs text-gray-400 pt-4 border-t border-gray-100">
          Data generated at {new Date(dashboardData.generated_at).toLocaleString()}
        </div>
      )}
    </div>
  );
}
